/**
 * Client-side resolution of the "what level does this student land at" display
 * for the Accelerated Testers override page.
 *
 * The authoritative resolution runs at report time in the backend builder
 * (functions/modules/Assessments/AcceleratedTesters/buildAcceleratedTesterTests.js).
 * This file DUPLICATES the cut-point table and precedence purely so the page can
 * render a resolved level without a round-trip. Keep the values in sync with
 * functions/modules/Assessments/AcceleratedTesters/cutPoints.js.
 *
 * Per-subject precedence (SPEC §5.2):
 *   override.excluded
 *     -> contributes nothing (Excluded)
 *   else best real score (override ACT competes with SAT; override SAT replaces ingested SAT)
 *     -> scored at that level
 *   else override.assumedLevel
 *     -> scored at that level, flagged projected
 *   else teaSatOrAct === false (math/reading only)
 *     -> non-participant
 *   else
 *     -> data gap
 */

// Mirror of functions/.../cutPoints.js AcceleratedTesterCutPoints[2026].
// Floor score of each band; below `approaches` = Does Not Meet (still scored).
const CLIENT_CUT_POINTS = {
  2026: {
    satEbrw: { approaches: 410, meets: 480, masters: 670, min: 200, max: 800 },
    satMath: { approaches: 440, meets: 530, masters: 690, min: 200, max: 800 },
    actEngRead: { approaches: 27, meets: 34, masters: 60, min: 2, max: 72 },
    actMath: { approaches: 16, meets: 21, masters: 30, min: 1, max: 36 },
    actScience: { approaches: 16, meets: 23, masters: 28, min: 1, max: 36 },
  },
};

const LEVEL_RANK = { doesNotMeet: 0, approaches: 1, meets: 2, masters: 3 };
const LEVEL_LABEL = {
  doesNotMeet: 'Does Not Meet',
  approaches: 'Approaches',
  meets: 'Meets',
  masters: 'Masters',
};

// Which conversion table each subject + score kind uses.
const TABLE_FOR = {
  reading: { sat: 'satEbrw', act: 'actEngRead' },
  math: { sat: 'satMath', act: 'actMath' },
  science: { sat: null, act: 'actScience' },
};

const getCutPointsForYear = (year) => {
  const key = Number(year);
  if (CLIENT_CUT_POINTS[key]) return CLIENT_CUT_POINTS[key];
  const years = Object.keys(CLIENT_CUT_POINTS)
    .map(Number)
    .sort((a, b) => a - b);
  return CLIENT_CUT_POINTS[years[years.length - 1]];
};

// Convert a single raw score against a named table -> { ammCode, rank } or null.
const convertScore = (score, tableName, cutSet) => {
  const table = cutSet[tableName];
  if (!table) return null;
  const n = Number(score);
  if (!Number.isFinite(n) || n <= 0) return null;
  if (n < table.min || n > table.max) return null;
  if (n >= table.masters) return { ammCode: 'masters', rank: 3 };
  if (n >= table.meets) return { ammCode: 'meets', rank: 2 };
  if (n >= table.approaches) return { ammCode: 'approaches', rank: 1 };
  return { ammCode: 'doesNotMeet', rank: 0 };
};

// Best real-score level for one subject. Each source's override REPLACES that
// source's ingested score; SAT and ACT candidates then compete (best-of).
// `sat` = { ebrw, math } ingested; `act` = { engReadSum, math, science } ingested.
const bestRealLevel = (subject, override = {}, sat = {}, act = {}, cutSet) => {
  const tables = TABLE_FOR[subject];
  if (!tables) return null;
  const candidates = [];

  if (subject === 'reading') {
    const satEbrw = override.satEbrw != null ? override.satEbrw : sat.ebrw;
    candidates.push(convertScore(satEbrw, 'satEbrw', cutSet));
    const actEngRead = override.actEngRead != null ? override.actEngRead : act.engReadSum;
    candidates.push(convertScore(actEngRead, 'actEngRead', cutSet));
  } else if (subject === 'math') {
    const satMath = override.satMath != null ? override.satMath : sat.math;
    candidates.push(convertScore(satMath, 'satMath', cutSet));
    const actMath = override.actMath != null ? override.actMath : act.math;
    candidates.push(convertScore(actMath, 'actMath', cutSet));
  } else if (subject === 'science') {
    const actScience = override.actScience != null ? override.actScience : act.science;
    candidates.push(convertScore(actScience, 'actScience', cutSet));
  }

  return candidates.filter(Boolean).reduce((best, cur) => (best == null || cur.rank > best.rank ? cur : best), null);
};

/**
 * Resolve one student × one subject to a display outcome.
 * Returns { status, level?, ammCode?, projected? } where status is one of:
 *   'excluded' | 'scored' | 'assumed' | 'nonParticipant' | 'dataGap'
 */
export const resolveSubject = (entry, subject, year) => {
  const override = (entry && entry.override) || {};
  if (override.excluded === true) {
    return { status: 'excluded' };
  }
  const cutSet = getCutPointsForYear(year);
  const sat = (entry && entry.sat) || {};
  const act = (entry && entry.act) || {};
  const real = bestRealLevel(subject, override, sat, act, cutSet);
  if (real) {
    return { status: 'scored', level: real.ammCode, ammCode: real.ammCode };
  }
  if (override.assumedLevel && LEVEL_RANK[override.assumedLevel] != null) {
    return {
      status: 'assumed',
      level: override.assumedLevel,
      ammCode: override.assumedLevel,
      projected: true,
    };
  }
  // No score from any source.
  if (entry && entry.teaSatOrAct === false) {
    // Non-participant is a math/reading concept only; science with no score
    // simply contributes nothing (and is not a "needs attention" worklist item).
    if (subject === 'math' || subject === 'reading') {
      return { status: 'nonParticipant' };
    }
    return { status: 'none' };
  }
  // teaSatOrAct true (or unknown) but no score in app -> TEA has it, we lack it.
  return { status: 'dataGap' };
};

const SCIENCE_ONLY = (subjects) => subjects && Object.keys(subjects).length === 1 && subjects.science != null;

/**
 * Roll a student's subjects up to a single row-level resolved-level label and
 * a coarse classification used for filtering/sorting/header counts.
 *
 * classification: 'scored' | 'assumed' | 'nonParticipant' | 'dataGap' | 'excluded'
 * needsAttention: true when any actionable subject is a data gap (the worklist).
 */
export const resolveStudent = (entry, year) => {
  const subjects = (entry && entry.subjects) || {};
  const keys = Object.keys(subjects).filter((k) => subjects[k] != null);
  const perSubject = {};
  keys.forEach((subject) => {
    perSubject[subject] = resolveSubject(entry, subject, year);
  });

  const override = (entry && entry.override) || {};
  if (override.excluded === true) {
    return {
      perSubject,
      classification: 'excluded',
      needsAttention: false,
      hasScore: false,
      label: 'Excluded',
    };
  }

  const statuses = Object.values(perSubject).map((r) => r.status);
  const hasScore = statuses.includes('scored');
  const hasAssumed = statuses.includes('assumed');
  const hasDataGap = statuses.includes('dataGap');
  const hasNonParticipant = statuses.includes('nonParticipant');

  // Pick the best resolved level among scored/assumed subjects for the label.
  let bestRank = -1;
  let bestAmm = null;
  let bestProjected = false;
  Object.values(perSubject).forEach((r) => {
    if ((r.status === 'scored' || r.status === 'assumed') && LEVEL_RANK[r.ammCode] > bestRank) {
      bestRank = LEVEL_RANK[r.ammCode];
      bestAmm = r.ammCode;
      bestProjected = r.status === 'assumed';
    }
  });

  let classification;
  let label;
  if (hasScore || hasAssumed) {
    classification = hasScore ? 'scored' : 'assumed';
    label = `${LEVEL_LABEL[bestAmm]}${bestProjected ? ' (PROJ)' : ''}`;
  } else if (hasDataGap) {
    classification = 'dataGap';
    label = 'data gap';
  } else if (hasNonParticipant) {
    classification = 'nonParticipant';
    label = 'Non-participant';
  } else if (SCIENCE_ONLY(subjects)) {
    // Science-only with no ACT score: not a TEA participation concept.
    classification = 'dataGap';
    label = 'data gap';
  } else {
    classification = 'dataGap';
    label = 'data gap';
  }

  return {
    perSubject,
    classification,
    // Single rolled-up level for the student (null unless scored/assumed) — lets the
    // header tally count one level per student instead of one per assumed subject.
    ammCode: bestAmm,
    // Needs attention = no real/assumed level AND TEA expects a result we lack.
    needsAttention: !hasScore && !hasAssumed && hasDataGap,
    hasScore,
    label,
  };
};

export const LEVEL_OPTIONS = [
  { value: 'doesNotMeet', label: 'Does Not Meet' },
  { value: 'approaches', label: 'Approaches' },
  { value: 'meets', label: 'Meets' },
  { value: 'masters', label: 'Masters' },
];

export const SUBJECT_LABEL = {
  math: 'Math',
  reading: 'RLA',
  science: 'Science',
};

export { LEVEL_LABEL, LEVEL_RANK };

export default 'acceleratedTesters.selectors.js';
